import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Injectable, inject} from '@angular/core';
import {ObjectTypeDescription} from './objecttypedescription';
import {BindingDescription} from './bindingdescription';
import {AttributeTypeDescription} from './attributetypedescription';
import {FilterResult} from '../../resource/filterresult';
import {BaseApiService} from '../../core/services/base-api.service';
import {SupportedLocaleConfigurationService} from '../uiconfiguration/supportedlocaleconfiguration.service';

@Injectable()
export class SchemaService extends BaseApiService {
  private localeService = inject(SupportedLocaleConfigurationService);

  public getObjectTypeDescriptions(): Observable<ObjectTypeDescription[]> {
    const attributes = [
      this.getAttr('DisplayName'),
      this.getAttr('Description'),
      this.getAttr('ObjectID'),
      this.getAttr('Name'),
    ].filter(attr => attr !== null);

    return this.getWithLocale<FilterResult>(
      '/' + this.getType('ObjectTypeDescription'),
      attributes,
      10000,
      this.localeService.getSupportedLocaleConfiguration(),
    ).pipe(map(response => this.toObjectTypeDescriptions(response.Results)));
  }

  public getAttributeTypeDescriptions(): Observable<AttributeTypeDescription[]> {
    const attributes = [
      this.getAttr('DisplayName'),
      this.getAttr('Description'),
      this.getAttr('ObjectID'),
      this.getAttr('Name'),
      this.getAttr('DataType'),
      this.getAttr('Multivalued'),
      this.getAttr('StringRegex'),
      this.getAttr('UsageKeyword'),
    ].filter(attr => attr !== null);

    return this.getWithLocale<FilterResult>(
      '/' + this.getType('AttributeTypeDescription'),
      attributes,
      10000,
      this.localeService.getSupportedLocaleConfiguration(),
    ).pipe(map(response => this.toAttributeTypeDescriptions(response.Results)));
  }

  public getBindingDescriptions(): Observable<BindingDescription[]> {
    const attributes = [
      this.getAttr('DisplayName'),
      this.getAttr('Description'),
      this.getAttr('ObjectID'),
      this.getAttr('BoundAttributeType'),
      this.getAttr('BoundObjectType'),
      this.getAttr('StringRegex'),
    ].filter(attr => attr !== null);

    return this.getWithLocale<FilterResult>(
      '/' + this.getType('BindingDescription'),
      attributes,
      10000,
      this.localeService.getSupportedLocaleConfiguration(),
    ).pipe(map(response => this.toBindingDescriptions(response.Results)));
  }

  private toObjectTypeDescriptions(resources: any): ObjectTypeDescription[] {
    let finalResources: ObjectTypeDescription[] = [];
    resources.forEach(obj => {
      finalResources.push(this.toObjectTypeDescription(obj));
    });
    return finalResources;
  }

  private toObjectTypeDescription(r: any): ObjectTypeDescription {
    return <ObjectTypeDescription>{
      displayname: r[this.getAttr('DisplayName')],
      description: r[this.getAttr('Description')],
      id: r[this.getAttr('ObjectID')],
      systemname: r[this.getAttr('Name')],
    };
  }

  private toAttributeTypeDescriptions(resources: any): AttributeTypeDescription[] {
    let finalAttributes: AttributeTypeDescription[] = [];
    resources.forEach(obj => {
      finalAttributes.push(this.toAttributeTypeDescription(obj));
    });
    return finalAttributes;
  }

  private toAttributeTypeDescription(r: any): AttributeTypeDescription {
    const usageKeywords = r[this.getAttr('UsageKeyword')];
    return <AttributeTypeDescription>{
      displayname: r[this.getAttr('DisplayName')],
      description: r[this.getAttr('Description')],
      id: r[this.getAttr('ObjectID')],
      systemname: r[this.getAttr('Name')],
      datatype: r[this.getAttr('DataType')],
      multivalued: r[this.getAttr('Multivalued')],
      regularexpression: r[this.getAttr('StringRegex')],
      filename: this.extractFileNameFromUsageKeywords(usageKeywords),
    };
  }

  private extractFileNameFromUsageKeywords(usageKeywords: string[]): string {
    if (usageKeywords == null || !Array.isArray(usageKeywords)) {
      return null;
    }
    const prefix = 'filename:';
    for (const keyword of usageKeywords) {
      if (keyword != null && keyword.toLowerCase().startsWith(prefix)) {
        return keyword.substring(prefix.length);
      }
    }
    return null;
  }

  private toBindingDescriptions(bindings: any): BindingDescription[] {
    let finalBindings: BindingDescription[] = [];
    bindings.forEach(obj => {
      finalBindings.push(this.toBindingDescription(obj));
    });
    return finalBindings;
  }

  private toBindingDescription(r: any): BindingDescription {
    return <BindingDescription>{
      displayname: r[this.getAttr('DisplayName')],
      description: r[this.getAttr('Description')],
      id: r[this.getAttr('ObjectID')],
      boundattributetype: r[this.getAttr('BoundAttributeType')],
      boundobjecttype: r[this.getAttr('BoundObjectType')],
      regularexpression: r[this.getAttr('StringRegex')],
    };
  }
}
